import { supabase } from '@/lib/supabase'
import { getQontoStatus, qontoApiCall } from '@/services/qontoApi'
import type {
  QontoBankAccount,
  QontoTransaction,
  SyncedTransaction,
  SyncLog,
  MatchResult,
  UnpaidAlert,
  UnpaidAlertThresholds,
  RecurringContract,
  RecurringContractFormData,
  TransactionFilters,
  UnpaidFilters,
  RecurringContractFilters,
  QontoStats,
  AccountingStats,
  RecurrenceFrequency,
} from '@/types/qonto'
import type { Invoice } from '@/types/invoice'

// ============================================
// STORY-039: Service API Qonto
// Les appels passent par le proxy OAuth du worker (qontoApi) :
// les tokens restent cote serveur et on evite le CORS de l'API Qonto.
// ============================================

interface QontoCredentials {
  login: string
  secretKey: string
}

export const qontoService = {
  // Verifier la connexion Qonto (statut OAuth cote worker)
  async testConnection(): Promise<boolean> {
    try {
      const status = await getQontoStatus()
      return status.connected
    } catch {
      return false
    }
  },

  // Recuperer les comptes bancaires
  async getBankAccounts(): Promise<QontoBankAccount[]> {
    const response = await qontoApiCall<{ organization: { bank_accounts: QontoBankAccount[] } }>(
      '/organization'
    )
    return response.organization?.bank_accounts ?? []
  },

  // Recuperer les transactions
  async getTransactions(params: {
    slug: string
    status?: string[]
    settled_at_from?: string
    settled_at_to?: string
    page?: number
    per_page?: number
  }): Promise<{ transactions: QontoTransaction[]; meta: { total_count: number; current_page: number; total_pages: number } }> {
    const proxyParams: Record<string, string> = {
      slug: params.slug,
      current_page: String(params.page || 1),
      per_page: String(params.per_page || 100),
    }

    // Le proxy ne transporte qu'une valeur par cle
    if (params.status && params.status.length > 0) {
      proxyParams['status[]'] = params.status[0]
    }
    if (params.settled_at_from) {
      proxyParams.settled_at_from = params.settled_at_from
    }
    if (params.settled_at_to) {
      proxyParams.settled_at_to = params.settled_at_to
    }

    return qontoApiCall('/transactions', { params: proxyParams })
  },

  // Sauvegarder les credentials Qonto
  async saveCredentials(credentials: QontoCredentials): Promise<void> {
    const { error } = await supabase
      .from('settings')
      .upsert({
        key: 'qonto_credentials',
        value: JSON.stringify(credentials),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' })

    if (error) throw error
  },

  // Supprimer les credentials
  async removeCredentials(): Promise<void> {
    const { error } = await supabase
      .from('settings')
      .delete()
      .eq('key', 'qonto_credentials')

    if (error) throw error
  },
}

// ============================================
// STORY-040: Service de synchronisation
// ============================================

export const syncService = {
  // Recuperer les transactions synchronisees
  async getTransactions(filters?: TransactionFilters): Promise<{ transactions: SyncedTransaction[]; total: number }> {
    let query = supabase
      .from('qonto_transactions')
      .select('*', { count: 'exact' })
      .order('emitted_at', { ascending: false })

    if (filters?.search) {
      query = query.or(`label.ilike.%${filters.search}%,reference.ilike.%${filters.search}%,counterparty_name.ilike.%${filters.search}%`)
    }

    if (filters?.dateFrom) {
      query = query.gte('emitted_at', filters.dateFrom)
    }

    if (filters?.dateTo) {
      query = query.lte('emitted_at', filters.dateTo)
    }

    if (filters?.side) {
      query = query.eq('side', filters.side)
    }

    if (filters?.status) {
      if (filters.status === 'matched') {
        query = query.eq('is_matched', true)
      } else if (filters.status === 'synced') {
        query = query.eq('is_matched', false)
      }
    }

    if (filters?.matched !== undefined) {
      if (filters.matched) {
        query = query.not('facture_id', 'is', null)
      } else {
        query = query.is('facture_id', null)
      }
    }

    const page = filters?.page ?? 1
    const limit = filters?.limit ?? 50
    const from = (page - 1) * limit
    query = query.range(from, from + limit - 1)

    const { data, error, count } = await query

    if (error) throw error
    return { transactions: data as SyncedTransaction[], total: count ?? 0 }
  },

  // Lancer une synchronisation
  async startSync(accountSlug: string, dateFrom?: string, dateTo?: string): Promise<SyncLog> {
    // Creer le log de sync
    const { data: syncLog, error: logError } = await supabase
      .from('qonto_sync_logs')
      .insert({
        sync_type: 'manual',
        started_at: new Date().toISOString(),
        status: 'running',
      })
      .select()
      .single()

    if (logError) throw logError

    try {
      // Recuperer les transactions depuis Qonto
      const { transactions: qontoTransactions } = await qontoService.getTransactions({
        slug: accountSlug,
        status: ['completed'],
        settled_at_from: dateFrom,
        settled_at_to: dateTo,
        per_page: 100,
      })

      let newCount = 0
      let matchedCount = 0
      let failedCount = 0

      for (const tx of qontoTransactions) {
        try {
          // Verifier si la transaction existe deja
          const { data: existing } = await supabase
            .from('qonto_transactions')
            .select('id')
            .eq('qonto_transaction_id', tx.transaction_id)
            .single()

          if (!existing) {
            // Nouvelle transaction - inserer
            const { error: insertError } = await supabase
              .from('qonto_transactions')
              .insert({
                qonto_transaction_id: tx.transaction_id,
                amount: tx.amount_cents,
                currency: tx.currency,
                side: tx.side,
                operation_type: tx.operation_type,
                status: tx.status,
                label: tx.label,
                reference: tx.reference,
                counterparty_name: tx.counterparty,
                emitted_at: tx.emitted_at,
                settled_at: tx.settled_at,
                is_matched: false,
              })

            if (insertError) {
              failedCount++
            } else {
              newCount++
            }
          }
        } catch {
          failedCount++
        }
      }

      // Tenter le rapprochement automatique
      matchedCount = await this.autoMatch()

      // Mettre a jour le log
      const { data: updatedLog, error: updateError } = await supabase
        .from('qonto_sync_logs')
        .update({
          completed_at: new Date().toISOString(),
          status: failedCount > 0 ? 'partial' : 'success',
          transactions_fetched: qontoTransactions.length,
          transactions_matched: matchedCount,
        })
        .eq('id', syncLog.id)
        .select()
        .single()

      if (updateError) throw updateError
      return updatedLog as SyncLog
    } catch (error) {
      // En cas d'erreur, mettre a jour le log
      await supabase
        .from('qonto_sync_logs')
        .update({
          completed_at: new Date().toISOString(),
          status: 'failed',
          error_message: error instanceof Error ? error.message : 'Unknown error',
        })
        .eq('id', syncLog.id)

      throw error
    }
  },

  // Rapprochement automatique
  async autoMatch(): Promise<number> {
    // Recuperer les transactions non rapprochees (credits uniquement)
    const { data: transactions } = await supabase
      .from('qonto_transactions')
      .select('*')
      .eq('side', 'credit')
      .eq('is_matched', false)
      .is('facture_id', null)

    if (!transactions?.length) return 0

    // Recuperer les factures non payees
    const { data: invoices } = await supabase
      .from('facture')
      .select('id, numero, total_ttc, date_echeance, utilisateur_id')
      .in('statut', ['sent', 'en_attente', 'overdue'])

    if (!invoices?.length) return 0

    let matchedCount = 0

    for (const tx of transactions) {
      const match = this.findBestMatch(tx, invoices)

      if (match && match.confidence !== 'low') {
        await supabase
          .from('qonto_transactions')
          .update({
            facture_id: match.invoiceId,
            match_confidence: match.confidence === 'high' ? 1 : match.confidence === 'medium' ? 0.5 : 0,
            is_matched: true,
          })
          .eq('id', tx.id)

        // Si haute confiance, marquer la facture comme payee
        if (match.confidence === 'high') {
          await supabase
            .from('facture')
            .update({
              statut: 'payee',
              date_paiement: tx.settled_at || tx.emitted_at,
              updated_at: new Date().toISOString(),
            })
            .eq('id', match.invoiceId)
        }

        matchedCount++
      }
    }

    return matchedCount
  },

  // Trouver le meilleur match pour une transaction
  findBestMatch(
    transaction: SyncedTransaction,
    invoices: Array<{ id: string; numero: string; total_ttc: number; date_echeance: string }>
  ): MatchResult | null {
    const results: MatchResult[] = []

    for (const invoice of invoices) {
      const txAmount = transaction.amount_cents ?? transaction.amount
      const amountMatch = txAmount === invoice.total_ttc

      // Chercher le numero de facture dans la reference ou le label
      const counterparty = transaction.counterparty ?? transaction.counterparty_name ?? ''
      const searchText = `${transaction.reference || ''} ${transaction.label} ${counterparty}`.toUpperCase()
      const referenceMatch = searchText.includes(invoice.numero.toUpperCase())

      // Calculer la proximite de date
      const txDate = new Date(transaction.emitted_at)
      const dueDate = new Date(invoice.date_echeance)
      const daysDiff = Math.abs(Math.floor((txDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)))

      if (amountMatch || referenceMatch) {
        let confidence: MatchResult['confidence'] = 'low'
        const reasons: string[] = []

        if (amountMatch && referenceMatch) {
          confidence = 'high'
          reasons.push('Montant et reference correspondent')
        } else if (amountMatch && daysDiff <= 7) {
          confidence = 'high'
          reasons.push('Montant correspond, date proche')
        } else if (referenceMatch) {
          confidence = 'medium'
          reasons.push('Reference correspond')
        } else if (amountMatch) {
          confidence = daysDiff <= 30 ? 'medium' : 'low'
          reasons.push('Montant correspond')
        }

        results.push({
          transactionId: transaction.id,
          invoiceId: invoice.id,
          confidence,
          reason: reasons.join(', '),
          amountMatch,
          referenceMatch,
          dateProximity: daysDiff,
        })
      }
    }

    // Retourner le meilleur match
    if (results.length === 0) return null

    return results.sort((a, b) => {
      const confidenceOrder = { high: 0, medium: 1, low: 2, manual: 3 }
      return confidenceOrder[a.confidence] - confidenceOrder[b.confidence]
    })[0]
  },

  // Rapprochement manuel
  async manualMatch(transactionId: string, invoiceId: string): Promise<void> {
    await supabase
      .from('qonto_transactions')
      .update({
        facture_id: invoiceId,
        match_confidence: 1,
        is_matched: true,
      })
      .eq('id', transactionId)

    // Marquer la facture comme payee
    const { data: tx } = await supabase
      .from('qonto_transactions')
      .select('settled_at, emitted_at')
      .eq('id', transactionId)
      .single()

    if (tx) {
      await supabase
        .from('facture')
        .update({
          statut: 'payee',
          date_paiement: tx.settled_at || tx.emitted_at,
          updated_at: new Date().toISOString(),
        })
        .eq('id', invoiceId)
    }
  },

  // Ignorer une transaction
  async ignoreTransaction(transactionId: string): Promise<void> {
    await supabase
      .from('qonto_transactions')
      .update({
        status: 'ignored',
      })
      .eq('id', transactionId)
  },

  // Annuler un rapprochement
  async unmatch(transactionId: string): Promise<void> {
    const { data: tx } = await supabase
      .from('qonto_transactions')
      .select('facture_id')
      .eq('id', transactionId)
      .single()

    if (tx?.facture_id) {
      // Remettre la facture en attente
      await supabase
        .from('facture')
        .update({
          statut: 'en_attente',
          date_paiement: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tx.facture_id)
    }

    await supabase
      .from('qonto_transactions')
      .update({
        facture_id: null,
        match_confidence: null,
        is_matched: false,
      })
      .eq('id', transactionId)
  },

  // Recuperer les logs de sync
  async getSyncLogs(limit = 10): Promise<SyncLog[]> {
    const { data, error } = await supabase
      .from('qonto_sync_logs')
      .select('*')
      .order('started_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data as SyncLog[]
  },

  // Statistiques Qonto
  async getStats(): Promise<QontoStats> {
    const { data: transactions } = await supabase
      .from('qonto_transactions')
      .select('is_matched, amount, side, status')

    const { data: lastSync } = await supabase
      .from('qonto_sync_logs')
      .select('completed_at')
      .eq('status', 'success')
      .order('completed_at', { ascending: false })
      .limit(1)
      .single()

    const txs = transactions || []

    return {
      totalBalance: 0, // A recuperer depuis Qonto API
      pendingTransactions: txs.filter(t => t.status === 'pending').length,
      syncedTransactions: txs.filter(t => !t.is_matched).length,
      matchedTransactions: txs.filter(t => t.is_matched).length,
      lastSyncAt: lastSync?.completed_at || null,
    }
  },
}

// ============================================
// STORY-041: Service de detection des impayes
// ============================================

export const unpaidService = {
  // Recuperer les alertes d'impayes
  async getAlerts(filters?: UnpaidFilters): Promise<{ alerts: UnpaidAlert[]; total: number }> {
    let query = supabase
      .from('unpaid_alerts')
      .select(`
        *,
        invoice:facture!invoice_id(
          id,
          numero,
          total_ttc,
          date_echeance,
          client:users!utilisateur_id(id, full_name, email, company)
        )
      `, { count: 'exact' })
      .order('days_overdue', { ascending: false })

    if (filters?.alertLevel) {
      query = query.eq('alert_level', filters.alertLevel)
    }

    if (filters?.alertStatus) {
      query = query.eq('alert_status', filters.alertStatus)
    }

    if (filters?.minDaysOverdue) {
      query = query.gte('days_overdue', filters.minDaysOverdue)
    }

    const page = filters?.page ?? 1
    const limit = filters?.limit ?? 20
    const from = (page - 1) * limit
    query = query.range(from, from + limit - 1)

    const { data, error, count } = await query

    if (error) throw error
    return { alerts: data as UnpaidAlert[], total: count ?? 0 }
  },

  // Detecter les impayes et creer les alertes
  async detectUnpaid(thresholds?: Partial<UnpaidAlertThresholds>): Promise<number> {
    const config: UnpaidAlertThresholds = {
      warningDays: thresholds?.warningDays ?? 7,
      dangerDays: thresholds?.dangerDays ?? 30,
      criticalDays: thresholds?.criticalDays ?? 60,
      autoReminder: thresholds?.autoReminder ?? true,
      reminderIntervalDays: thresholds?.reminderIntervalDays ?? 7,
      maxReminders: thresholds?.maxReminders ?? 3,
    }

    // Recuperer les factures en retard
    const today = new Date()
    const { data: overdueInvoices } = await supabase
      .from('facture')
      .select('id, utilisateur_id, total_ttc, date_echeance')
      .in('statut', ['sent', 'en_attente'])
      .lt('date_echeance', today.toISOString().split('T')[0])

    if (!overdueInvoices?.length) return 0

    let alertsCreated = 0

    for (const invoice of overdueInvoices) {
      const dueDate = new Date(invoice.date_echeance)
      const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))

      // Determiner le niveau d'alerte
      let alertLevel: 'warning' | 'danger' | 'critical' = 'warning'
      if (daysOverdue >= config.criticalDays) {
        alertLevel = 'critical'
      } else if (daysOverdue >= config.dangerDays) {
        alertLevel = 'danger'
      }

      // Verifier si une alerte existe deja
      const { data: existingAlert } = await supabase
        .from('unpaid_alerts')
        .select('id, alert_level')
        .eq('invoice_id', invoice.id)
        .in('alert_status', ['active', 'acknowledged'])
        .single()

      if (existingAlert) {
        // Mettre a jour l'alerte existante si le niveau change
        if (existingAlert.alert_level !== alertLevel) {
          await supabase
            .from('unpaid_alerts')
            .update({
              alert_level: alertLevel,
              days_overdue: daysOverdue,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingAlert.id)
        }
      } else {
        // Creer une nouvelle alerte
        const { error } = await supabase
          .from('unpaid_alerts')
          .insert({
            invoice_id: invoice.id,
            client_id: invoice.utilisateur_id,
            alert_level: alertLevel,
            alert_status: 'active',
            days_overdue: daysOverdue,
            amount_due_cents: invoice.total_ttc,
          })

        if (!error) {
          alertsCreated++

          // Mettre a jour le statut de la facture
          await supabase
            .from('facture')
            .update({
              statut: 'overdue',
              updated_at: new Date().toISOString(),
            })
            .eq('id', invoice.id)
        }
      }
    }

    return alertsCreated
  },

  // Acquitter une alerte
  async acknowledgeAlert(alertId: string): Promise<void> {
    await supabase
      .from('unpaid_alerts')
      .update({
        alert_status: 'acknowledged',
        updated_at: new Date().toISOString(),
      })
      .eq('id', alertId)
  },

  // Resoudre une alerte (paiement recu)
  async resolveAlert(alertId: string): Promise<void> {
    const { data: alert } = await supabase
      .from('unpaid_alerts')
      .select('invoice_id')
      .eq('id', alertId)
      .single()

    if (alert) {
      await supabase
        .from('facture')
        .update({
          statut: 'payee',
          date_paiement: new Date().toISOString().split('T')[0],
          updated_at: new Date().toISOString(),
        })
        .eq('id', alert.invoice_id)
    }

    await supabase
      .from('unpaid_alerts')
      .update({
        alert_status: 'resolved',
        updated_at: new Date().toISOString(),
      })
      .eq('id', alertId)
  },

  // Escalader une alerte
  async escalateAlert(alertId: string, notes?: string): Promise<void> {
    await supabase
      .from('unpaid_alerts')
      .update({
        alert_status: 'escalated',
        notes: notes || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', alertId)
  },

  // Envoyer une relance pour une alerte
  async sendReminder(alertId: string): Promise<void> {
    const { data: alert } = await supabase
      .from('unpaid_alerts')
      .select('invoice_id, reminder_count')
      .eq('id', alertId)
      .single()

    if (!alert) throw new Error('Alert not found')

    // Enregistrer la relance
    await supabase
      .from('notification_logs')
      .insert({
        type: 'reminder_sent',
        titre: 'Relance impaye envoyee',
        message: `Relance #${(alert.reminder_count || 0) + 1} envoyee`,
        reference_type: 'facture',
        reference_id: alert.invoice_id,
      })

    // Mettre a jour l'alerte
    await supabase
      .from('unpaid_alerts')
      .update({
        reminder_count: (alert.reminder_count || 0) + 1,
        last_reminder_sent_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', alertId)
  },

  // Recuperer les seuils de configuration
  async getThresholds(): Promise<UnpaidAlertThresholds> {
    const { data } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'unpaid_thresholds')
      .single()

    const defaults: UnpaidAlertThresholds = {
      warningDays: 7,
      dangerDays: 30,
      criticalDays: 60,
      autoReminder: true,
      reminderIntervalDays: 7,
      maxReminders: 3,
    }

    if (!data?.value) return defaults
    try {
      const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value
      return parsed as UnpaidAlertThresholds
    } catch {
      return defaults
    }
  },

  // Sauvegarder les seuils
  async saveThresholds(thresholds: UnpaidAlertThresholds): Promise<void> {
    await supabase
      .from('settings')
      .upsert({
        key: 'unpaid_thresholds',
        value: JSON.stringify(thresholds),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' })
  },
}

// ============================================
// STORY-042: Service des paiements recurrents
// ============================================

export const recurringService = {
  // Recuperer les contrats recurrents
  async getContracts(filters?: RecurringContractFilters): Promise<{ contracts: RecurringContract[]; total: number }> {
    let query = supabase
      .from('contracts')
      .select(`
        *,
        client:users!client_id(id, full_name, email, company)
      `, { count: 'exact' })
      .order('next_billing_date', { ascending: true })

    if (filters?.search) {
      query = query.ilike('title', `%${filters.search}%`)
    }

    if (filters?.status) {
      query = query.eq('status', filters.status)
    }

    if (filters?.frequency) {
      query = query.eq('billing_frequency', filters.frequency)
    }

    if (filters?.clientId) {
      query = query.eq('client_id', filters.clientId)
    }

    const page = filters?.page ?? 1
    const limit = filters?.limit ?? 20
    const from = (page - 1) * limit
    query = query.range(from, from + limit - 1)

    const { data, error, count } = await query

    if (error) throw error
    return { contracts: data as RecurringContract[], total: count ?? 0 }
  },

  // Recuperer un contrat par ID
  async getById(id: string): Promise<RecurringContract | null> {
    const { data, error } = await supabase
      .from('contracts')
      .select(`
        *,
        client:users!client_id(*)
      `)
      .eq('id', id)
      .single()

    if (error) throw error
    return data as RecurringContract
  },

  // Creer un nouveau contrat
  async create(data: RecurringContractFormData): Promise<RecurringContract> {
    const { data: contract, error } = await supabase
      .from('contracts')
      .insert({
        client_id: data.client_id,
        title: data.title,
        description: data.description,
        amount: data.amount_ht_cents,
        billing_frequency: data.frequency,
        start_date: data.start_date,
        end_date: data.end_date,
        next_billing_date: data.next_invoice_date,
        status: data.status,
      })
      .select()
      .single()

    if (error) throw error
    return contract as RecurringContract
  },

  // Mettre a jour un contrat
  async update(id: string, data: Partial<RecurringContractFormData>): Promise<RecurringContract> {
    const { data: contract, error } = await supabase
      .from('contracts')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return contract as RecurringContract
  },

  // Changer le statut d'un contrat
  async updateStatus(id: string, status: RecurringContract['status']): Promise<void> {
    await supabase
      .from('contracts')
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
  },

  // Supprimer un contrat
  async delete(id: string): Promise<void> {
    await supabase
      .from('contracts')
      .delete()
      .eq('id', id)
  },

  // Generer les factures pour les contrats actifs
  async generateDueInvoices(): Promise<{ generated: number; errors: string[] }> {
    const today = new Date()

    // Recuperer les contrats qui doivent generer une facture
    const { data: contracts } = await supabase
      .from('contracts')
      .select('*')
      .eq('status', 'active')
      .lte('next_billing_date', today.toISOString().split('T')[0])

    if (!contracts?.length) {
      return { generated: 0, errors: [] }
    }

    let generated = 0
    const errors: string[] = []

    for (const contract of contracts) {
      try {
        await this.generateInvoice(contract.id)
        generated++
      } catch (error) {
        errors.push(`Contrat ${contract.title}: ${error instanceof Error ? error.message : 'Erreur inconnue'}`)
      }
    }

    return { generated, errors }
  },

  // Generer une facture pour un contrat
  async generateInvoice(contractId: string): Promise<Invoice> {
    const contract = await this.getById(contractId)
    if (!contract) throw new Error('Contract not found')

    // Calculer les totaux
    const totalHt = contract.amount_ht_cents ?? contract.amount ?? 0
    const tvaRate = contract.tva_rate ?? 20
    const tva = Math.round(totalHt * tvaRate / 100)
    const totalTtc = totalHt + tva

    // Generer le numero de facture
    const year = new Date().getFullYear()
    const { count } = await supabase
      .from('facture')
      .select('*', { count: 'exact', head: true })
      .gte('date_creation', `${year}-01-01`)

    const numero = `FAC-${year}-${String((count || 0) + 1).padStart(4, '0')}`

    // Calculer la periode
    const freq = contract.frequency ?? contract.billing_frequency ?? 'monthly'
    const periodStart = new Date(contract.next_invoice_date ?? contract.next_billing_date)
    const periodEnd = this.calculateNextDate(periodStart, freq as RecurrenceFrequency)
    periodEnd.setDate(periodEnd.getDate() - 1)

    // Preparer les lignes
    const lignes = [
      {
        id: crypto.randomUUID(),
        description: `${contract.title} - Periode du ${periodStart.toLocaleDateString('fr-FR')} au ${periodEnd.toLocaleDateString('fr-FR')}`,
        quantite: 1,
        prix_unitaire: totalHt / 100,
        taux_tva: tvaRate,
        total_ht: totalHt / 100,
      },
    ]

    // Creer la facture
    const { data: invoice, error } = await supabase
      .from('facture')
      .insert({
        numero,
        utilisateur_id: contract.client_id,
        date_creation: new Date().toISOString().split('T')[0],
        date_echeance: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        total_ht: totalHt,
        tva,
        total_ttc: totalTtc,
        produits_json: lignes,
        conditions: 'Paiement a reception de facture',
        statut: 'sent',
      })
      .select()
      .single()

    if (error) throw error

    // Enregistrer dans l'historique
    await supabase
      .from('contract_invoice_history')
      .insert({
        contract_id: contractId,
        invoice_id: invoice.id,
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0],
        generated_at: new Date().toISOString(),
        status: 'generated',
      })

    // Mettre a jour le contrat
    const nextDate = this.calculateNextDate(new Date(contract.next_invoice_date ?? contract.next_billing_date), freq as RecurrenceFrequency)

    await supabase
      .from('contracts')
      .update({
        next_billing_date: nextDate.toISOString().split('T')[0],
        updated_at: new Date().toISOString(),
      })
      .eq('id', contractId)

    // Verifier si le contrat doit etre termine
    if (contract.end_date && nextDate > new Date(contract.end_date)) {
      await this.updateStatus(contractId, 'completed')
    }

    return invoice as Invoice
  },

  // Calculer la prochaine date selon la frequence
  calculateNextDate(date: Date, frequency: RecurrenceFrequency): Date {
    const next = new Date(date)
    switch (frequency) {
      case 'monthly':
        next.setMonth(next.getMonth() + 1)
        break
      case 'quarterly':
        next.setMonth(next.getMonth() + 3)
        break
      case 'biannual':
        next.setMonth(next.getMonth() + 6)
        break
      case 'annual':
        next.setFullYear(next.getFullYear() + 1)
        break
    }
    return next
  },

  // Recuperer l'historique des factures d'un contrat
  async getInvoiceHistory(contractId: string): Promise<{ history: Array<{ invoice: Invoice; period_start: string; period_end: string; generated_at: string }>; total: number }> {
    const { data, error, count } = await supabase
      .from('contract_invoice_history')
      .select(`
        *,
        invoice:facture!invoice_id(*)
      `, { count: 'exact' })
      .eq('contract_id', contractId)
      .order('generated_at', { ascending: false })

    if (error) throw error
    return {
      history: data as Array<{ invoice: Invoice; period_start: string; period_end: string; generated_at: string }>,
      total: count ?? 0,
    }
  },

  // Statistiques des paiements recurrents
  async getStats(): Promise<{
    activeContracts: number
    totalMonthlyRevenue: number
    upcomingInvoices: number
    pausedContracts: number
  }> {
    const { data: contracts } = await supabase
      .from('contracts')
      .select('status, amount, billing_frequency')

    const active = contracts?.filter(c => c.status === 'active') || []
    const paused = contracts?.filter(c => c.status === 'paused') || []

    // Calculer le revenu mensuel normalise
    const monthlyRevenue = active.reduce((sum, c) => {
      const amount = (c.amount ?? 0) / 100
      switch (c.billing_frequency) {
        case 'monthly': return sum + amount
        case 'quarterly': return sum + amount / 3
        case 'biannual': return sum + amount / 6
        case 'annual': return sum + amount / 12
        default: return sum
      }
    }, 0)

    // Contrats avec facture a generer dans les 7 prochains jours
    const nextWeek = new Date()
    nextWeek.setDate(nextWeek.getDate() + 7)

    const { count: upcoming } = await supabase
      .from('contracts')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .lte('next_billing_date', nextWeek.toISOString().split('T')[0])

    return {
      activeContracts: active.length,
      totalMonthlyRevenue: monthlyRevenue,
      upcomingInvoices: upcoming || 0,
      pausedContracts: paused.length,
    }
  },
}

// ============================================
// Statistiques globales comptabilite
// ============================================

export const accountingService = {
  async getStats(): Promise<AccountingStats> {
    // Impayes
    const { data: unpaidInvoices } = await supabase
      .from('facture')
      .select('total_ttc, statut')
      .in('statut', ['sent', 'en_attente', 'overdue'])

    const unpaid = unpaidInvoices || []
    const overdue = unpaid.filter(i => i.statut === 'overdue')

    // Alertes
    const { data: alerts } = await supabase
      .from('unpaid_alerts')
      .select('alert_level')
      .eq('alert_status', 'active')

    const alertsCount = {
      warning: alerts?.filter(a => a.alert_level === 'warning').length || 0,
      danger: alerts?.filter(a => a.alert_level === 'danger').length || 0,
      critical: alerts?.filter(a => a.alert_level === 'critical').length || 0,
    }

    // Recurrents
    const recurringStats = await recurringService.getStats()

    return {
      unpaidTotal: unpaid.reduce((sum, i) => sum + (i.total_ttc || 0), 0),
      unpaidCount: unpaid.length,
      overdueTotal: overdue.reduce((sum, i) => sum + (i.total_ttc || 0), 0),
      overdueCount: overdue.length,
      recurringMonthlyRevenue: recurringStats.totalMonthlyRevenue,
      activeContracts: recurringStats.activeContracts,
      alertsCount,
    }
  },
}
